import { useEffect, useMemo, useState } from 'react';
import { ulid } from 'ulid';
import { ApiCallError, call } from '@/api.ts';
import type { Shell, ShellTemplate } from '@/api.ts';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert.tsx';
import { Badge } from '@/components/ui/badge.tsx';
import { Button } from '@/components/ui/button.tsx';
import { ConfirmDialog } from '@/components/ui/confirm-dialog.tsx';
import { EmptyState } from '@/components/ui/empty-state.tsx';
import { Input, Label } from '@/components/ui/input.tsx';
import { Segmented } from '@/components/ui/segmented.tsx';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import {
  cleanMounts, cleanNav, errorFor, moveItem, parseRaw, removeAt, REGIONS, replaceAt, toRaw,
  validateShellConfig,
} from '@/lib/shell-config.ts';
import type { Mount, NavItem } from '@/lib/shell-config.ts';
import { deleteRefusal, saveRefusal, SELECTABLE_TEMPLATES, shellSubline } from '@/lib/shell-summary.ts';
import { refusal, roleRefusal } from '@/lib/refusal.ts';
import { canWrite, useAuthenticated } from '@/session.tsx';
import { useShells } from '@/hooks.ts';

/**
 * The shells list, and the form that edits one.
 *
 * A rail and a form rather than a table and a modal: there are a handful of shells, the operator
 * is comparing them while editing one, and the thing they are editing is long enough that hiding
 * the list behind it loses the comparison.
 */
export function ShellsPage() {
  const session = useAuthenticated();
  const shells = useShells(session);
  const writable = canWrite(session.role);
  const cannotEdit = roleRefusal(session.role, 'change shells', writable);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** A shell being created has no server row yet, so it lives here until saved. */
  const [draftShell, setDraftShell] = useState<Shell | null>(null);
  const [deleting, setDeleting] = useState<Shell | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  const items = useMemo(
    () => (draftShell ? [...(shells.items ?? []), draftShell] : (shells.items ?? [])),
    [shells.items, draftShell],
  );

  // Land on something rather than an empty right-hand column. The default shell is the one an
  // operator almost always came for.
  useEffect(() => {
    if (selectedId || items.length === 0) return;
    setSelectedId((items.find((shell) => shell.isDefault) ?? items[0])!.shellId);
  }, [items, selectedId]);

  const selected = items.find((shell) => shell.shellId === selectedId) ?? null;

  /**
   * A new shell is a `PUT` to an id we mint.
   *
   * §6.2 defines no `POST /shells`: `PUT /shells/{shellId}` creates or updates, so the id is the
   * caller's to choose. A ULID keeps it the same shape as every other id in the system rather
   * than introducing a second convention only the console uses.
   */
  function startNewShell() {
    const created: Shell = {
      shellId: ulid(),
      slug: '',
      template: 'static',
      isDefault: false,
      config: { name: '', nav: [], mounts: [] },
    };
    setDraftShell(created);
    setSelectedId(created.shellId);
  }

  async function save(shell: Shell) {
    setStatus(null);
    try {
      await call(session, `/shells/${shell.shellId}`, {
        method: 'PUT',
        body: JSON.stringify({
          slug: shell.slug,
          template: shell.template,
          isDefault: shell.isDefault,
          config: {
            ...shell.config,
            nav: cleanNav(shell.config.nav),
            mounts: cleanMounts(shell.config.mounts),
          },
        }),
      });
      setDraftShell(null);
      shells.reload();
      setStatus({ tone: 'ok', text: 'Saved. The dataplane picks this up within the config cache TTL.' });
    } catch (cause) {
      // The control plane's one error shape, surfaced verbatim: an operator who sees
      // "no-default-shell" can act on it, and a paraphrase would lose that.
      setStatus({ tone: 'error', text: describe(cause) });
    }
  }

  async function remove(shell: Shell) {
    setBusy(true);
    setStatus(null);
    try {
      await call(session, `/shells/${shell.shellId}`, { method: 'DELETE' });
      setDeleting(null);
      setSelectedId(null);
      shells.reload();
      setStatus({ tone: 'ok', text: `Deleted ${shell.slug}.` });
    } catch (cause) {
      setStatus({ tone: 'error', text: describe(cause) });
    } finally {
      setBusy(false);
    }
  }

  if (shells.error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load shells</AlertTitle>
        <AlertDescription>{shells.error}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="text-[34px] leading-tight font-semibold tracking-[-0.02em]">Shells</h1>
        <p className="max-w-[62ch] text-sm leading-relaxed">
          The document each tenant host renders. Exactly one shell per organization is the default,
          and it is the one a host falls back to when nothing else matches.
        </p>
      </header>

      {status ? (
        <div
          className={
            status.tone === 'ok'
              ? 'border-l-[3px] border-[color:var(--color-primary)] bg-[color:var(--color-accent)] px-3.5 py-3'
              : 'border-l-[3px] border-[color:var(--color-destructive)] bg-[color:rgba(214,0,108,0.08)] px-3.5 py-3'
          }
        >
          <p className="text-[13.5px] leading-relaxed">{status.text}</p>
        </div>
      ) : null}

      <div className="grid gap-8 lg:grid-cols-[440px_1fr] lg:gap-12">
        {/* Left: the rail. */}
        <div className="grid content-start gap-4">
          {!shells.items ? (
            <div className="grid gap-3">
              {[0, 1, 2].map((row) => <Skeleton key={row} className="h-4 w-full" />)}
            </div>
          ) : items.length === 0 ? (
            <EmptyState title="No shells" description="Every organization is created with one." />
          ) : (
            <ul className="divide-border grid divide-y border-y border-[color:var(--color-border)]">
              {items.map((shell) => {
                const active = shell.shellId === selectedId;
                return (
                  <li key={shell.shellId}>
                    <button
                      type="button"
                      aria-current={active ? 'true' : undefined}
                      onClick={() => setSelectedId(shell.shellId)}
                      // Negative margin so the selected tint bleeds the full width of the rail
                      // rather than sitting in a box inside it.
                      className={`-mx-3 grid w-[calc(100%+1.5rem)] gap-1 px-3 py-3.5 text-left ${
                        active ? 'bg-[color:var(--color-accent)]' : 'hover:bg-muted'
                      }`}
                    >
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-base font-semibold">{shell.config.name || 'Untitled shell'}</span>
                        <Badge variant="secondary">{shell.template}</Badge>
                        {shell.isDefault ? <Badge className="ml-auto">default</Badge> : null}
                        {shell === draftShell ? <Badge variant="secondary" className="ml-auto">unsaved</Badge> : null}
                      </span>
                      <span className="text-muted-foreground font-mono text-[12px]">{shellSubline(shell)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div>
            <Button
              variant="outline"
              size="sm"
              disabled={!writable || Boolean(draftShell)}
              title={refusal(
                cannotEdit,
                draftShell ? 'Finish or discard the unsaved shell first.' : null,
              )}
              onClick={startNewShell}
            >
              New shell
            </Button>
          </div>
        </div>

        {/* Right: the form. */}
        {selected ? (
          <ShellForm
            key={selected.shellId}
            shell={selected}
            canEdit={writable}
            refusalReason={cannotEdit}
            isDraft={selected === draftShell}
            onDiscard={() => { setDraftShell(null); setSelectedId(null); }}
            onDelete={() => setDeleting(selected)}
            onSave={save}
          />
        ) : (
          <div className="text-muted-foreground text-sm">Pick a shell to edit it.</div>
        )}
      </div>

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => { if (!open) setDeleting(null); }}
        title={`Delete ${deleting?.slug ?? ''}?`}
        description={
          <>
            Any host resolving to this shell will stop rendering and return{' '}
            <code className="font-mono text-xs">unknown tenant</code>. Published fragments are not
            affected. This cannot be undone.
          </>
        }
        confirmLabel="Delete shell"
        destructive
        busy={busy}
        onConfirm={() => { if (deleting) void remove(deleting); }}
      />
    </div>
  );
}

/**
 * The shell editor.
 *
 * This replaces two `<textarea>`s of hand-written JSON whose only feedback was a parse error on
 * submit — the worst screen in the console. Nav items and mounts are ordered lists that the
 * template renders in order, so they are edited as ordered lists: add, remove, and move, with
 * validation that names the row it is complaining about.
 *
 * The raw JSON is still reachable behind a toggle. A form cannot express a field a newer control
 * plane understands, and an operator tool that traps you inside its own widgets is worse than one
 * that offers a way around them.
 */
function ShellForm({
  shell, canEdit, refusalReason, isDraft, onDiscard, onDelete, onSave,
}: {
  shell: Shell;
  canEdit: boolean;
  refusalReason: string | null;
  isDraft: boolean;
  onDiscard(): void;
  onDelete(): void;
  onSave(shell: Shell): void | Promise<void>;
}) {
  const [draft, setDraft] = useState<Shell>(shell);
  const [showRaw, setShowRaw] = useState(false);
  const [rawText, setRawText] = useState(() => toRaw(shell.config.nav, shell.config.mounts));
  const [rawError, setRawError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const { nav, mounts } = draft.config;
  const errors = useMemo(() => validateShellConfig(nav, mounts), [nav, mounts]);
  // Errors compute on every change and appear once, on submit, then live-update. Validating from
  // the first keystroke shouts at someone who has simply not finished typing yet.
  const visible = submitted ? errors : [];

  function setNav(next: NavItem[]) {
    setDraft({ ...draft, config: { ...draft.config, nav: next } });
  }
  function setMounts(next: Mount[]) {
    setDraft({ ...draft, config: { ...draft.config, mounts: next } });
  }

  function applyRaw() {
    const parsed = parseRaw(rawText);
    if ('error' in parsed) {
      setRawError(parsed.error);
      return;
    }
    setRawError(null);
    setDraft({ ...draft, config: { ...draft.config, nav: parsed.nav, mounts: parsed.mounts } });
    setShowRaw(false);
  }

  const cannotDelete = deleteRefusal(draft);
  const cannotSave = saveRefusal(draft);

  return (
    <form
      className="grid min-w-0 content-start gap-7"
      onSubmit={(event) => {
        event.preventDefault();
        setSubmitted(true);
        if (errors.length === 0) void onSave(draft);
      }}
    >
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[color:var(--color-border)] pb-2.5">
        <div className="grid gap-0.5">
          <h2 className="text-[25px] leading-tight font-semibold tracking-[-0.01em]">
            {draft.config.name || (isDraft ? 'New shell' : 'Untitled shell')}
          </h2>
          <p className="text-muted-foreground font-mono text-[13px]">{draft.slug || 'no slug yet'}</p>
        </div>
        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            className="accent-primary size-4"
            checked={showRaw}
            onChange={(event) => {
              if (event.target.checked) setRawText(toRaw(nav, mounts));
              setRawError(null);
              setShowRaw(event.target.checked);
            }}
          />
          Raw JSON
        </label>
      </div>

      {submitted && errors.length > 0 ? (
        <Alert variant="destructive">
          <AlertTitle>
            {errors.length} {errors.length === 1 ? 'problem' : 'problems'} to fix
          </AlertTitle>
          <AlertDescription>Each one is marked on the field it belongs to.</AlertDescription>
        </Alert>
      ) : null}

      <fieldset className="grid gap-5" disabled={!canEdit}>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Slug" htmlFor="shell-slug">
            <Input
              id="shell-slug"
              value={draft.slug}
              className="font-mono text-[13px]"
              onChange={(event) => setDraft({ ...draft, slug: event.target.value })}
              required
            />
          </Field>
          <Field label="Name" htmlFor="shell-name">
            <Input
              id="shell-name"
              value={draft.config.name}
              onChange={(event) => setDraft({ ...draft, config: { ...draft.config, name: event.target.value } })}
              required
            />
          </Field>

          {/* The design's four-way chooser. Three are selectable; `byo` appears only when the
              shell already is one, shown as chosen and refused at once — it is a real current
              value that this form may not set, because a BYO shell is established by pointing at
              an origin that is fetched and checked first. */}
          <Field
            label="Template"
            htmlFor={`shell-template-${draft.template}`}
            hint={cannotSave ?? 'How the dataplane renders this shell\u2019s document.'}
          >
            <Segmented
              name="shell-template"
              value={draft.template}
              disabled={!canEdit}
              options={[
                ...SELECTABLE_TEMPLATES.map((template) => ({ value: template, label: template })),
                ...(draft.template === 'byo'
                  ? [{ value: 'byo', label: 'byo', disabled: true, reason: cannotSave ?? undefined }]
                  : []),
              ]}
              onChange={(value) => setDraft({ ...draft, template: value as ShellTemplate })}
            />
          </Field>

          <Field label="Brand colour" htmlFor="shell-brand" hint="Any CSS colour. Applied through one custom property.">
            <Input
              id="shell-brand"
              value={draft.config.brand?.color ?? ''}
              placeholder="#0b7285"
              className="font-mono text-[13px]"
              onChange={(event) => setDraft({
                ...draft,
                config: { ...draft.config, brand: { ...draft.config.brand, color: event.target.value } },
              })}
            />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-[13px]">
          <input
            type="checkbox"
            className="accent-primary size-4"
            checked={draft.isDefault}
            onChange={(event) => setDraft({ ...draft, isDefault: event.target.checked })}
          />
          Default shell for this organization
        </label>

        {showRaw ? (
          <div className="grid gap-2">
            {rawError ? (
              <Alert variant="destructive">
                <AlertDescription>{rawError}</AlertDescription>
              </Alert>
            ) : null}
            <textarea
              aria-label="Navigation and mounts as JSON"
              rows={16}
              value={rawText}
              onChange={(event) => setRawText(event.target.value)}
              className="border-input bg-card rounded-[var(--radius)] border p-2 font-mono text-xs"
            />
            <Button type="button" variant="secondary" size="sm" className="justify-self-start" onClick={applyRaw}>
              Apply to the form
            </Button>
          </div>
        ) : (
          <>
            <ListSection
              title="Navigation"
              description="Rendered in this order in the shell header."
              count={nav.length}
              empty="No navigation links. The header will show the brand alone."
              action={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canEdit}
                  title={refusal(refusalReason)}
                  onClick={() => setNav([...nav, { label: '', href: '/' }])}
                >
                  Add link
                </Button>
              }
            >
              {nav.map((item, index) => (
                <Row
                  key={index}
                  index={index}
                  total={nav.length}
                  columns="sm:grid-cols-[1fr_1fr]"
                  canEdit={canEdit}
                  refusalReason={refusalReason}
                  onMove={(to) => setNav(moveItem(nav, index, to))}
                  onRemove={() => setNav(removeAt(nav, index))}
                  removeLabel={`Remove link ${index + 1}`}
                >
                  <Field label="Label" htmlFor={`nav-${index}-label`} error={errorFor(visible, `nav.${index}.label`)}>
                    <Input
                      id={`nav-${index}-label`}
                      value={item.label}
                      aria-invalid={Boolean(errorFor(visible, `nav.${index}.label`))}
                      onChange={(event) => setNav(replaceAt(nav, index, { ...item, label: event.target.value }))}
                    />
                  </Field>
                  <Field label="Link" htmlFor={`nav-${index}-href`} error={errorFor(visible, `nav.${index}.href`)}>
                    <Input
                      id={`nav-${index}-href`}
                      value={item.href}
                      className="font-mono text-[13px]"
                      aria-invalid={Boolean(errorFor(visible, `nav.${index}.href`))}
                      onChange={(event) => setNav(replaceAt(nav, index, { ...item, href: event.target.value }))}
                    />
                  </Field>
                </Row>
              ))}
            </ListSection>

            <ListSection
              title="Mounts"
              description="Each slot is where a fragment of the same id composes. Order decides render order within a region."
              count={mounts.length}
              empty="No mounts. This shell renders its own chrome and composes nothing."
              action={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!canEdit}
                  title={refusal(refusalReason)}
                  onClick={() => setMounts([...mounts, { slot: '', region: 'main' }])}
                >
                  Add mount
                </Button>
              }
            >
              {mounts.map((mount, index) => {
                const slotError = errorFor(visible, `mounts.${index}.slot`);
                return (
                  <Row
                    key={index}
                    index={index}
                    total={mounts.length}
                    columns="sm:grid-cols-[1fr_160px_140px]"
                    canEdit={canEdit}
                    refusalReason={refusalReason}
                    onMove={(to) => setMounts(moveItem(mounts, index, to))}
                    onRemove={() => setMounts(removeAt(mounts, index))}
                    removeLabel={`Remove mount ${index + 1}`}
                    // A duplicate slot is not a validation nicety: `<fragment-slot name>` is how
                    // the gateway matches, so the second one silently never fills. It gets the
                    // magenta strip, and the strip names the row it collides with.
                    problem={slotError}
                  >
                    <Field label="Slot" htmlFor={`mount-${index}-slot`} hint="Matches a fragment id.">
                      <Input
                        id={`mount-${index}-slot`}
                        value={mount.slot}
                        className={`font-mono text-[13px] ${slotError ? 'border-[color:var(--color-destructive)]' : ''}`}
                        aria-invalid={Boolean(slotError)}
                        onChange={(event) => setMounts(replaceAt(mounts, index, { ...mount, slot: event.target.value }))}
                      />
                    </Field>
                    <Field label="Region" htmlFor={`mount-${index}-region`}>
                      {/* A native select: three options, fully accessible, keyboard-native on
                          every platform, and zero bundle. A Radix listbox here would be cost
                          without gain. */}
                      <select
                        id={`mount-${index}-region`}
                        value={mount.region}
                        disabled={!canEdit}
                        onChange={(event) =>
                          setMounts(replaceAt(mounts, index, { ...mount, region: event.target.value as Mount['region'] }))}
                        className="border-input bg-background h-9 w-full rounded-[var(--radius)] border px-2 text-[13px]"
                      >
                        {REGIONS.map((region) => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>
                    </Field>
                    <Field
                      label="Source"
                      htmlFor={`mount-${index}-src`}
                      error={errorFor(visible, `mounts.${index}.src`)}
                      hint="Widgets only."
                    >
                      <Input
                        id={`mount-${index}-src`}
                        value={mount.src ?? ''}
                        placeholder="/panel"
                        className="font-mono text-[13px]"
                        aria-invalid={Boolean(errorFor(visible, `mounts.${index}.src`))}
                        onChange={(event) => setMounts(replaceAt(mounts, index, { ...mount, src: event.target.value }))}
                      />
                    </Field>
                  </Row>
                );
              })}
            </ListSection>
          </>
        )}
      </fieldset>

      <div className="flex flex-wrap items-center gap-2 border-t border-[color:var(--color-border)] pt-4">
        <Button
          type="submit"
          disabled={!canEdit || Boolean(cannotSave)}
          title={refusal(refusalReason, cannotSave)}
        >
          Save shell
        </Button>
        {isDraft ? (
          <Button type="button" variant="outline" onClick={onDiscard}>Discard</Button>
        ) : (
          <Button
            type="button"
            variant="outline"
            className="text-destructive ml-auto"
            disabled={!canEdit || Boolean(cannotDelete)}
            title={refusal(refusalReason, cannotDelete)}
            onClick={onDelete}
          >
            Delete shell
          </Button>
        )}
      </div>
    </form>
  );
}

function Field({
  label, htmlFor, hint, error, children,
}: {
  label: string;
  htmlFor: string;
  /** `| undefined` rather than optional: `errorFor` returns undefined and
      `exactOptionalPropertyTypes` treats "absent" and "present and undefined" as different. */
  hint?: string | undefined;
  error?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="grid min-w-0 content-start gap-1.5">
      <Label htmlFor={htmlFor}>{label}</Label>
      {children}
      {error ? (
        <p className="text-destructive text-xs">{error}</p>
      ) : hint ? (
        <p className="text-muted-foreground text-xs leading-relaxed">{hint}</p>
      ) : null}
    </div>
  );
}

function ListSection({
  title, description, count, empty, action, children,
}: {
  title: string;
  description: string;
  count: number;
  empty: string;
  action: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <fieldset className="grid gap-3">
      <div className="flex flex-wrap items-end justify-between gap-3 border-b border-[color:var(--color-border)] pb-2">
        <div className="grid gap-0.5">
          <legend className="text-[11px] font-medium tracking-[0.12em] uppercase">{title}</legend>
          <p className="text-muted-foreground text-[12.5px] leading-relaxed">{description}</p>
        </div>
        {action}
      </div>
      {count === 0 ? (
        <p className="text-muted-foreground border border-dashed border-[color:var(--color-border)] p-4 text-[13px]">
          {empty}
        </p>
      ) : (
        <div className="grid gap-4">{children}</div>
      )}
    </fieldset>
  );
}

/**
 * One editable row, with reorder and remove.
 *
 * Buttons rather than drag-and-drop: order matters here, and a keyboard user reordering a list
 * should not need a pointer. It is also one fewer dependency.
 */
function Row({
  index, total, columns, canEdit, refusalReason, onMove, onRemove, removeLabel, problem, children,
}: {
  index: number;
  total: number;
  columns: string;
  canEdit: boolean;
  refusalReason: string | null;
  onMove(to: number): void;
  onRemove(): void;
  removeLabel: string;
  problem?: string | undefined;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-2">
      <div className="grid gap-3 sm:grid-cols-[1fr_90px]">
        <div className={`grid min-w-0 gap-3 ${columns}`}>{children}</div>
        <div className="flex items-end gap-1">
          <Button
            type="button" variant="ghost" size="sm"
            disabled={index === 0 || !canEdit}
            title={refusal(refusalReason)}
            aria-label={`Move up (row ${index + 1})`}
            onClick={() => onMove(index - 1)}
          >
            ↑
          </Button>
          <Button
            type="button" variant="ghost" size="sm"
            disabled={index === total - 1 || !canEdit}
            title={refusal(refusalReason)}
            aria-label={`Move down (row ${index + 1})`}
            onClick={() => onMove(index + 1)}
          >
            ↓
          </Button>
          <Button
            type="button" variant="ghost" size="sm"
            disabled={!canEdit}
            title={refusal(refusalReason)}
            aria-label={removeLabel}
            onClick={onRemove}
          >
            ×
          </Button>
        </div>
      </div>

      {problem ? (
        <p className="border-l-[3px] border-[color:var(--color-destructive)] bg-[color:rgba(214,0,108,0.08)] px-3 py-2 text-[12.5px]">
          {problem}
        </p>
      ) : null}
    </div>
  );
}

function describe(cause: unknown): string {
  return cause instanceof ApiCallError ? `${cause.code}: ${cause.message}` : String(cause);
}
